/**
 * SUNTRONIX ONE — Legal Agreements Admin (super_admin only)
 *
 * Manage the current + historical versions of the three agreements
 * (NDA, DPA, Portal Access Terms) and audit the acceptance ledger.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, Plus, ShieldCheck, CheckCircle2, Users, RefreshCw, Loader2, Download } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CODES = [
  { code: "NDA",           title: "Mutual Confidentiality Agreement", scope: "CORPORATE" },
  { code: "DPA",           title: "Data Protection Acknowledgement",  scope: "CORPORATE" },
  { code: "PORTAL_ACCESS", title: "Portal Access Terms",              scope: "INDIVIDUAL" },
];

const NUDGE_STATUS_TONE = {
  ACCEPTED: "bg-emerald-100 text-emerald-800",
  PENDING:  "bg-amber-100 text-amber-800",
};

const EMAIL_STATUS_TONE = {
  SENT:           "bg-emerald-100 text-emerald-800",
  MOCKED_LOGGED:  "bg-slate-100 text-slate-700",
  FAILED:         "bg-rose-100 text-rose-800",
  SKIPPED:        "bg-amber-100 text-amber-800",
};

export default function LegalAgreementsAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("templates");
  const [agreements, setAgreements] = useState([]);
  const [acceptances, setAcceptances] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [openCampaign, setOpenCampaign] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/legal/agreements"),
        api.get("/legal/acceptances", { params: { limit: 500 } }),
        api.get("/legal/campaigns"),
      ]);
      setAgreements(a.data?.items || []);
      setAcceptances(b.data?.items || []);
      setCampaigns(c.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const perCode = {};
    for (const c of CODES) perCode[c.code] = { current: null, versions: 0 };
    for (const a of agreements) {
      const b = perCode[a.code];
      if (b) {
        b.versions += 1;
        if (a.is_current) b.current = a;
      }
    }
    return perCode;
  }, [agreements]);

  const acceptanceStats = useMemo(() => {
    return {
      total: acceptances.length,
      corporate: acceptances.filter((a) => a.scope === "CORPORATE").length,
      individual: acceptances.filter((a) => a.scope === "INDIVIDUAL").length,
      last: acceptances[0]?.accepted_at,
    };
  }, [acceptances]);

  if (user && user.role !== "super_admin") {
    return (
      <div className="p-8 text-center text-slate-500 text-sm">
        Super-admin only.
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-4" data-testid="legal-admin">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Administration</div>
          <h1 className="text-2xl font-semibold text-slate-900">Legal Acceptance Framework</h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Corporate NDA / DPA and Portal Access Terms. Every acceptance is captured with IP, browser, timestamp and an evidence hash.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="legal-admin-refresh"
            className="h-8 px-3 rounded-md border border-slate-200 text-[12px] text-slate-600 hover:bg-slate-50 flex items-center gap-1.5"
          >
            <RefreshCw className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} /> Refresh
          </button>
          <button
            onClick={() => setShowNew(true)}
            data-testid="legal-admin-new"
            className="h-8 px-3 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 flex items-center gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" /> New version
          </button>
        </div>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={ShieldCheck} label="Templates" value={agreements.length} tone="slate" />
        <Kpi icon={FileText} label="Current versions" value={Object.values(stats).filter((s) => s.current).length + " / 3"} tone="indigo" />
        <Kpi icon={CheckCircle2} label="Acceptances (total)" value={acceptanceStats.total} tone="emerald" />
        <Kpi icon={Users} label="Individual · Corporate" value={`${acceptanceStats.individual} · ${acceptanceStats.corporate}`} tone="amber" />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[
          { id: "templates",   label: "Templates" },
          { id: "acceptances", label: `Acceptances (${acceptances.length})` },
          { id: "campaigns",   label: `Amendment campaigns (${campaigns.length})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`legal-tab-${t.id}`}
            className={
              "h-9 px-3 text-[12px] font-medium border-b-2 -mb-px " +
              (tab === t.id ? "text-slate-900 border-slate-900" : "text-slate-500 border-transparent hover:text-slate-900")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "templates" && (
        <TemplatesTable
          codes={CODES}
          stats={stats}
          agreements={agreements}
        />
      )}
      {tab === "acceptances" && <AcceptancesTable items={acceptances} />}
      {tab === "campaigns" && (
        <CampaignsTable
          items={campaigns}
          onOpen={(c) => setOpenCampaign(c)}
        />
      )}

      {showNew && (
        <NewAgreementDialog
          onClose={() => setShowNew(false)}
          onCreated={async () => { setShowNew(false); await load(); }}
        />
      )}
      {openCampaign && (
        <CampaignDrawer
          campaign={openCampaign}
          onClose={() => setOpenCampaign(null)}
        />
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }) {
  const tones = {
    slate:   "text-slate-700 bg-slate-100",
    indigo:  "text-indigo-700 bg-indigo-100",
    emerald: "text-emerald-700 bg-emerald-100",
    amber:   "text-amber-700 bg-amber-100",
  };
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 flex items-center gap-3">
      <div className={"h-9 w-9 rounded-md grid place-items-center " + (tones[tone] || tones.slate)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</div>
        <div className="text-[15px] font-semibold text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
}

function TemplatesTable({ codes, stats, agreements }) {
  return (
    <div className="space-y-4" data-testid="legal-templates-tab">
      {codes.map((c) => {
        const st = stats[c.code] || { current: null, versions: 0 };
        const history = agreements.filter((a) => a.code === c.code).sort((a, b) => b.created_at.localeCompare(a.created_at));
        return (
          <div key={c.code} className="rounded-md border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{c.scope} · {c.code}</div>
              <div className="text-[14px] font-semibold text-slate-900 flex-1">{c.title}</div>
              {st.current ? (
                <span className="inline-flex items-center gap-1 h-6 px-2 rounded bg-emerald-100 text-emerald-800 text-[11px] uppercase tracking-[0.14em]">
                  <CheckCircle2 className="h-3 w-3" /> {st.current.version} current
                </span>
              ) : (
                <span className="inline-flex items-center h-6 px-2 rounded bg-amber-100 text-amber-800 text-[11px] uppercase tracking-[0.14em]">No current version</span>
              )}
            </div>
            <div className="divide-y divide-slate-100">
              {history.length === 0 && (
                <div className="px-4 py-6 text-center text-slate-400 text-[12px]">No versions published yet</div>
              )}
              {history.map((v) => (
                <div key={v.id} className="px-4 py-2.5 flex items-center gap-3">
                  <div className="text-[13px] font-medium text-slate-900 w-16">{v.version}</div>
                  <div className="flex-1 text-[12px] text-slate-500 truncate">{v.summary || v.title}</div>
                  <div className="text-[11px] text-slate-400">{new Date(v.created_at).toLocaleDateString()}</div>
                  {v.is_current && <span className="text-[10px] text-emerald-700 uppercase tracking-[0.14em]">current</span>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AcceptancesTable({ items }) {
  const downloadPdf = async (a) => {
    try {
      const res = await api.get(`/legal/acceptances/${a.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open signed copy");
    }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="legal-acceptances-tab">
      <table className="w-full text-[12px]">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-[0.16em]">
          <tr>
            <th className="text-left px-3 py-2">When</th>
            <th className="text-left px-3 py-2">Code · Version</th>
            <th className="text-left px-3 py-2">Scope</th>
            <th className="text-left px-3 py-2">Signer</th>
            <th className="text-left px-3 py-2">Tenant</th>
            <th className="text-left px-3 py-2">IP</th>
            <th className="text-left px-3 py-2">Email</th>
            <th className="text-left px-3 py-2">Evidence hash</th>
            <th className="text-right px-3 py-2">Signed copy</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No acceptances yet</td></tr>
          )}
          {items.map((a) => (
            <tr key={a.id} className="border-t border-slate-100">
              <td className="px-3 py-2 whitespace-nowrap text-slate-600">{new Date(a.accepted_at).toLocaleString()}</td>
              <td className="px-3 py-2 font-medium text-slate-900">{a.agreement_code} · {a.agreement_version}</td>
              <td className="px-3 py-2">
                <span className={"inline-flex h-5 px-1.5 rounded text-[10px] uppercase tracking-[0.14em] " + (a.scope === "CORPORATE" ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-700")}>
                  {a.scope}
                </span>
              </td>
              <td className="px-3 py-2 text-slate-700">
                <div className="font-medium">{a.user_name}</div>
                <div className="text-[10px] text-slate-500">{a.user_email} {a.signer_title ? `· ${a.signer_title}` : ""}</div>
              </td>
              <td className="px-3 py-2 text-slate-600 truncate max-w-[160px]" title={a.master_company_id}>{a.master_company_id || "—"}</td>
              <td className="px-3 py-2 text-slate-500">{a.ip || "—"}</td>
              <td className="px-3 py-2 text-slate-500">
                {a.pdf_email_status ? (
                  <span className={"inline-flex h-5 px-1.5 rounded text-[10px] uppercase tracking-[0.14em] " +
                    (a.pdf_email_status === "SENT" ? "bg-emerald-100 text-emerald-800"
                     : a.pdf_email_status === "MOCKED_LOGGED" ? "bg-slate-100 text-slate-700"
                     : a.pdf_email_status.startsWith("SKIPPED") ? "bg-amber-100 text-amber-800"
                     : "bg-rose-100 text-rose-800")}>
                    {a.pdf_email_status.replace("MOCKED_LOGGED", "LOGGED")}
                  </span>
                ) : "—"}
              </td>
              <td className="px-3 py-2 font-mono text-[10px] text-slate-500 truncate max-w-[160px]" title={a.evidence_hash}>{a.evidence_hash?.slice(0, 16)}…</td>
              <td className="px-3 py-2 text-right">
                <button
                  type="button"
                  onClick={() => downloadPdf(a)}
                  data-testid={`legal-pdf-${a.id}`}
                  className="inline-flex items-center gap-1 h-7 px-2 rounded border border-slate-200 text-[11px] text-slate-700 hover:bg-slate-50"
                >
                  <Download className="h-3 w-3" /> PDF
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CampaignsTable({ items, onOpen }) {
  if (!items.length) {
    return (
      <div className="rounded-md border border-slate-200 bg-white p-8 text-center text-slate-400 text-[12px]" data-testid="legal-campaigns-tab">
        No amendment campaigns yet. Publishing a new version of an existing agreement will automatically fire one.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="legal-campaigns-tab">
      {items.map((c) => {
        const total = c.total_affected || 0;
        const done = c.completed || 0;
        const pct = total ? Math.round((done / total) * 100) : 0;
        return (
          <button
            key={c.id}
            onClick={() => onOpen(c)}
            data-testid={`legal-campaign-${c.id}`}
            className="w-full text-left rounded-md border border-slate-200 bg-white p-4 hover:border-slate-400 transition"
          >
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-indigo-100 text-indigo-700 grid place-items-center shrink-0">
                <FileText className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-slate-900">
                  {c.agreement_code} {c.agreement_version} <span className="text-slate-400 font-normal">← {c.previous_version}</span>
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {c.agreement_title} · published {new Date(c.published_at).toLocaleString()} by {c.published_by_email || "system"}
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={"h-full transition-all " + (pct === 100 ? "bg-emerald-500" : "bg-slate-900")}
                         style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[11px] text-slate-600 whitespace-nowrap">{done}/{total} · {pct}%</div>
                </div>
                <div className="mt-2 flex items-center gap-2 text-[10px]">
                  <StatChip label="Sent"    value={c.emails_sent}    tone="emerald" />
                  <StatChip label="Failed"  value={c.emails_failed}  tone="rose" />
                  <StatChip label="Skipped" value={c.emails_skipped || 0} tone="amber" />
                  <StatChip label="Scope"   value={c.scope}          tone="slate" />
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function StatChip({ label, value, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    rose:    "bg-rose-50 text-rose-800 border-rose-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <span className={"inline-flex items-center gap-1 h-5 px-1.5 rounded border text-[10px] " + (tones[tone] || tones.slate)}>
      <span className="uppercase tracking-[0.14em] opacity-70">{label}</span>
      <span className="font-semibold">{value}</span>
    </span>
  );
}

function CampaignDrawer({ campaign, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    api.get(`/legal/campaigns/${campaign.id}`)
      .then((r) => { if (alive) setDetail(r.data); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load campaign"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [campaign.id]);

  return (
    <div className="fixed inset-0 z-[9990] bg-slate-900/50 backdrop-blur-sm flex justify-end" onClick={onClose}
         data-testid="legal-campaign-drawer">
      <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 sticky top-0 bg-white z-10">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Amendment campaign</div>
            <div className="text-[14px] font-semibold text-slate-900 truncate">
              {campaign.agreement_code} {campaign.agreement_version} · {campaign.agreement_title}
            </div>
          </div>
          <button onClick={onClose} data-testid="legal-campaign-close" className="h-8 px-3 rounded border border-slate-200 text-[12px] text-slate-500 hover:bg-slate-50">Close</button>
        </header>
        <div className="p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-4 w-4 animate-spin text-slate-500" /></div>
          ) : detail ? (
            <>
              <div className="grid grid-cols-4 gap-2">
                <MiniStat label="Affected" value={detail.campaign.total_affected} />
                <MiniStat label="Sent"     value={detail.campaign.emails_sent}    tone="emerald" />
                <MiniStat label="Failed"   value={detail.campaign.emails_failed}  tone="rose" />
                <MiniStat label="Completed" value={detail.campaign.completed}     tone="indigo" />
              </div>
              <div className="rounded-md border border-slate-200 overflow-hidden">
                <table className="w-full text-[12px]">
                  <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-[0.14em]">
                    <tr>
                      <th className="text-left px-3 py-2">User</th>
                      <th className="text-left px-3 py-2">Tenant</th>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-left px-3 py-2">Nudge</th>
                      <th className="text-left px-3 py-2">Re-accepted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.nudges.map((n) => (
                      <tr key={n.id} className="border-t border-slate-100">
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-900">{n.user_name || n.user_email}</div>
                          <div className="text-[10px] text-slate-500">{n.user_email}</div>
                        </td>
                        <td className="px-3 py-2 truncate max-w-[140px] text-slate-500" title={n.master_company_id}>{n.master_company_id || "—"}</td>
                        <td className="px-3 py-2">
                          <span className={"inline-flex h-5 px-1.5 rounded text-[10px] uppercase tracking-[0.14em] " + (EMAIL_STATUS_TONE[n.email_status] || "bg-slate-100 text-slate-500")}>
                            {(n.email_status || "PENDING").replace("MOCKED_LOGGED", "LOGGED")}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={"inline-flex h-5 px-1.5 rounded text-[10px] uppercase tracking-[0.14em] " + (NUDGE_STATUS_TONE[n.status] || "bg-slate-100 text-slate-500")}>
                            {n.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                          {n.accepted_at ? new Date(n.accepted_at).toLocaleString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const tones = {
    default: "text-slate-800",
    emerald: "text-emerald-700",
    rose:    "text-rose-700",
    indigo:  "text-indigo-700",
  };
  return (
    <div className="rounded border border-slate-200 bg-white p-2">
      <div className="text-[9px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className={"text-[16px] font-semibold " + (tones[tone] || tones.default)}>{value}</div>
    </div>
  );
}

function NewAgreementDialog({ onClose, onCreated }) {
  const [code, setCode] = useState("PORTAL_ACCESS");
  const [title, setTitle] = useState("");
  const [version, setVersion] = useState("v1.1");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (body.trim().length < 20) { toast.error("Body must be at least 20 characters"); return; }
    setBusy(true);
    try {
      await api.post("/legal/agreements", {
        code, title: title.trim(), version: version.trim(),
        body_html: body, summary: summary.trim() || undefined,
      });
      toast.success(`${code} ${version} published — previous version marked historical.`);
      await onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[9990] bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4">
      <form onSubmit={submit} className="w-full max-w-2xl bg-white rounded-md shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-testid="legal-new-dialog">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
          <div className="text-[14px] font-semibold text-slate-900 flex-1">Publish new agreement version</div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900 text-[13px]">Cancel</button>
        </header>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Code</label>
              <select value={code} onChange={(e) => setCode(e.target.value)} data-testid="legal-new-code"
                className="w-full h-9 px-2 mt-1 rounded-md border border-slate-300 bg-white text-[13px]">
                {CODES.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.title}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Version</label>
              <input value={version} onChange={(e) => setVersion(e.target.value)} required
                data-testid="legal-new-version"
                className="w-full h-9 px-2 mt-1 rounded-md border border-slate-300 bg-white text-[13px]" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required
              placeholder={CODES.find((c) => c.code === code)?.title}
              data-testid="legal-new-title"
              className="w-full h-9 px-2 mt-1 rounded-md border border-slate-300 bg-white text-[13px]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Short summary</label>
            <input value={summary} onChange={(e) => setSummary(e.target.value)}
              data-testid="legal-new-summary"
              className="w-full h-9 px-2 mt-1 rounded-md border border-slate-300 bg-white text-[13px]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Body (HTML) · minimum 20 chars</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={10}
              data-testid="legal-new-body"
              placeholder="<h2>…</h2><p>…</p>"
              className="w-full mt-1 px-2 py-1.5 rounded-md border border-slate-300 bg-white text-[12px] font-mono resize-y" />
          </div>
        </div>
        <footer className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-slate-300 text-[12px]">Cancel</button>
          <button type="submit" disabled={busy} data-testid="legal-new-submit"
            className="h-9 px-4 rounded-md bg-slate-900 text-white text-[12px] font-medium disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Publish version"}
          </button>
        </footer>
      </form>
    </div>
  );
}
